import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { uploadToStorage } from '../uploadToStorage';

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [student, setStudent] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCalls, setExpandedCalls] = useState(new Set()); // Track expanded call IDs

  // Call logging state
  const [showCallForm, setShowCallForm] = useState(false);
  const [callFile, setCallFile] = useState(null);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [callStatus, setCallStatus] = useState('connected');
  const [callNotes, setCallNotes] = useState('');
  const [callTag, setCallTag] = useState('');
  const [uploading, setUploading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);

  // Mentor editing state
  const [isEditingMentor, setIsEditingMentor] = useState(false);
  const [mentorsList, setMentorsList] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [updatingMentor, setUpdatingMentor] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!showCallForm) {
      setCallFile(null);
      setScreenshotFile(null);
      setCallStatus('connected');
      setCallNotes('');
      setCallTag('');
      setUploading(false);
      setUploadProgress(0);
    }
  }, [showCallForm]);

  // Get call range from env (default 7 days)
  const callRangeDays = parseInt(process.env.REACT_APP_CALL_RANGE_DAYS || '7', 10);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    const [studentRes, callsRes] = await Promise.all([
      supabase
        .from('students')
        .select('id,name,phone,unique_id,whatsapp,parent_number,course,campus,hostel,mentor_id,created_at, mentor:mentors(full_name,email)')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('calls')
        .select('id,created_at,notes,call_recording_url,screenshots_url,mentor_id,call_status, mentor:mentors(full_name, email)')
        .eq('student_id', id)
        .order('created_at', { ascending: false }),
    ]);

    if (studentRes.error) {
      console.error('Error loading student', studentRes.error);
      setError('Unable to load student details.');
      setStudent(null);
    } else {
      setStudent(studentRes.data ?? null);
    }

    if (callsRes.error) {
      console.error('Error loading call history', callsRes.error);
      setCalls([]);
    } else {
      const callsData = callsRes.data ?? [];

      const resolveObjectPath = (value) => {
        if (!value) return null;
        if (!value.startsWith('http')) return value;
        try {
          const url = new URL(value);
          const afterObject = url.pathname.split('/object/')[1];
          if (!afterObject) return null;
          const segments = afterObject.split('/');
          // expected pattern: public|sign, bucket, ...objectPath
          if (segments.length < 3) return null;
          const [, bucketName, ...rest] = segments;
          if (bucketName !== 'calls') return null;
          return rest.join('/');
        } catch (parseError) {
          console.warn('Unable to parse storage URL', value, parseError);
          return null;
        }
      };

      const enhancedCalls = await Promise.all(
        callsData.map(async (call) => {
          const callPath = resolveObjectPath(call.call_recording_url);
          const screenshotPath = resolveObjectPath(call.screenshots_url);

          let recordingUrl = null;
          if (callPath) {
            const { data: signed, error: signedError } = await supabase
              .storage
              .from('calls')
              .createSignedUrl(callPath, 60 * 60);
            if (!signedError && signed?.signedUrl) {
              recordingUrl = signed.signedUrl;
            } else if (signedError) {
              console.warn('Unable to generate signed URL for recording', signedError);
            }
          }

          let screenshotUrl = null;
          if (screenshotPath) {
            const { data: signed, error: signedError } = await supabase
              .storage
              .from('calls')
              .createSignedUrl(screenshotPath, 60 * 60);
            if (!signedError && signed?.signedUrl) {
              screenshotUrl = signed.signedUrl;
            } else if (signedError) {
              console.warn('Unable to generate signed URL for screenshot', signedError);
            }
          }

          return {
            ...call,
            recordingUrl,
            screenshotUrl,
          };
        }),
      );

      setCalls(enhancedCalls);
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch mentors for dropdown
  const fetchMentors = useCallback(async () => {
    if (mentorsList.length > 0) return;
    try {
      const { data, error } = await supabase
        .from('mentors')
        .select('id, full_name, email')
        .order('full_name');

      if (error) throw error;
      setMentorsList(data || []);
    } catch (err) {
      console.error('Error fetching mentors', err);
    }
  }, [mentorsList.length]);

  const handleEditMentorClick = async () => {
    await fetchMentors();
    setSelectedMentor(student?.mentor_id || '');
    setIsEditingMentor(true);
  };

  const handleUpdateMentor = async () => {
    if (!selectedMentor) return;
    setUpdatingMentor(true);
    try {
      const { error } = await supabase
        .from('students')
        .update({ mentor_id: selectedMentor })
        .eq('id', student.id);

      if (error) throw error;

      setIsEditingMentor(false);
      fetchData(); // Refetch student data to show new mentor
      alert('Mentor updated successfully');
    } catch (err) {
      console.error('Error updating mentor', err);
      alert('Failed to update mentor');
    } finally {
      setUpdatingMentor(false);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const connectedCalls = calls.filter(call => call.call_status === 'connected');
    const lastConnectedCall = connectedCalls.length > 0 ? connectedCalls[0] : null;

    // Sort calls by date (most recent first) to analyze consecutive not connected calls
    const sortedCalls = [...calls].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Count consecutive not connected calls from the end
    let consecutiveNotConnected = 0;
    for (let i = 0; i < sortedCalls.length; i++) {
      if (sortedCalls[i].call_status === 'not_connected') {
        consecutiveNotConnected++;
      } else {
        break; // Stop at first non-not_connected call
      }
    }

    // Calculate next call date based on scheduling logic
    let nextCallDate;
    const lastCallDate = sortedCalls.length > 0 ? new Date(sortedCalls[0].created_at) : new Date(student?.created_at || Date.now());

    if (sortedCalls.length === 0) {
      // New student, never called before - schedule immediately
      nextCallDate = new Date();
    } else if (consecutiveNotConnected === 1) {
      // 1st not connected: schedule for next day
      nextCallDate = new Date(lastCallDate);
      nextCallDate.setDate(nextCallDate.getDate() + 1);
    } else if (consecutiveNotConnected === 2) {
      // 2nd not connected: schedule for next day (2 days from first not connected)
      nextCallDate = new Date(lastCallDate);
      nextCallDate.setDate(nextCallDate.getDate() + 1);
    } else if (consecutiveNotConnected >= 3) {
      // 3rd+ not connected: schedule for default range from last not connected call
      nextCallDate = new Date(lastCallDate);
      nextCallDate.setDate(nextCallDate.getDate() + callRangeDays);
    } else {
      // Student has connected calls, use default range from last call
      nextCallDate = new Date(lastCallDate);
      nextCallDate.setDate(nextCallDate.getDate() + callRangeDays);
    }

    return {
      totalConnected: connectedCalls.length,
      lastCallDate: lastConnectedCall ? new Date(lastConnectedCall.created_at) : null,
      nextCallDate,
    };
  }, [calls, student, callRangeDays]);

  const toggleCallExpansion = (callId) => {
    setExpandedCalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleFileChange = (e, setter) => {
    setter(e.target.files[0]);
  };

  const handleUploadCall = async () => {
    if (!student) {
      alert('Student information not available.');
      return;
    }

    // Validation based on call status
    if (callStatus === 'connected') {
      if (!callFile) {
        alert('Call recording is required for connected calls.');
        return;
      }
    } else if (callStatus === 'not_connected') {
      if (!screenshotFile) {
        alert('Screenshot is required when call status is "Not Connected".');
        return;
      }
    }

    // File size validation
    const maxFileSize = 50 * 1024 * 1024; // 50MB Supabase limit
    if (callFile && callFile.size > maxFileSize) {
      alert(`Call recording file is too large. Maximum size is 50MB. Your file is ${(callFile.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }
    if (screenshotFile && screenshotFile.size > maxFileSize) {
      alert(`Screenshot file is too large. Maximum size is 50MB. Your file is ${(screenshotFile.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }

    // Warn about large files
    const largeFileThreshold = 10 * 1024 * 1024; // 10MB
    if ((callFile && callFile.size > largeFileThreshold) || (screenshotFile && screenshotFile.size > largeFileThreshold)) {
      const largeFiles = [];
      if (callFile && callFile.size > largeFileThreshold) {
        largeFiles.push(`recording: ${(callFile.size / (1024 * 1024)).toFixed(2)}MB`);
      }
      if (screenshotFile && screenshotFile.size > largeFileThreshold) {
        largeFiles.push(`screenshot: ${(screenshotFile.size / (1024 * 1024)).toFixed(2)}MB`);
      }
      const proceed = window.confirm(`You are uploading large file(s): ${largeFiles.join(', ')}. This may take longer. Continue?`);
      if (!proceed) return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let callObjectPath = null;
      if (callFile) {
        callObjectPath = await uploadToStorage('calls', callFile, `calls/${student.id}/`, (loaded, total) => {
          setUploadProgress((loaded / total) * 50); // First 50% for call recording
        });
      }

      let screenshotObjectPath = null;
      if (screenshotFile) {
        screenshotObjectPath = await uploadToStorage('calls', screenshotFile, `calls/${student.id}/`, (loaded, total) => {
          const baseProgress = callFile ? 50 : 0; // Start from 50% if call recording was uploaded
          setUploadProgress(baseProgress + (loaded / total) * 40); // Next 40% for screenshot
        });
      }

      setUploadProgress(90); // Almost done with uploads

      const { error } = await supabase
        .from('calls')
        .insert({
          student_id: student.id,
          mentor_id: user.id, // Use the current user's ID as mentor
          call_recording_url: callObjectPath,
          screenshots_url: screenshotObjectPath,
          call_status: callStatus,
          notes: callNotes.trim() || null,
          tags: callTag || null,
        });

      if (error) throw error;

      setUploadProgress(100);

      alert('Call logged successfully!');

      // Reset form
      setCallFile(null);
      setScreenshotFile(null);
      setCallStatus('connected');
      setCallNotes('');
      setCallTag('');
      setShowCallForm(false);

      // Refetch data to update the call history
      fetchData();
    } catch (err) {
      console.error('Failed to upload call', err);
      alert('Error uploading call: ' + err.message);
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const mentorLabel = useMemo(() => {
    if (!student?.mentor_id) return 'Unassigned';
    return student.mentor?.full_name || student.mentor?.email || 'Assigned mentor';
  }, [student]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        Loading student information…
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 dark:border-rose-400/40 dark:bg-rose-500/10 dark:text-rose-200">
          {error || 'Student not found.'}
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-brand/40 hover:text-brand dark:border-slate-700 dark:text-slate-200"
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Student profile</p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">{student.name}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            EPS ID: {student.unique_id} · Mentor: {mentorLabel}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {role === 'mentor' && (
            <button
              type="button"
              onClick={() => setShowCallForm(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-brand bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/50"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Log Call
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(role === 'mentor' ? '/mentor/students' : '/students')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-brand/40 hover:text-brand dark:border-slate-700 dark:text-slate-200"
          >
            ← Back to {role === 'mentor' ? 'my students' : 'students'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 dark:text-green-400">Connected Calls</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.totalConnected}</p>
              {stats.lastCallDate && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Last: {formatDate(stats.lastCallDate)}
                </p>
              )}
            </div>
            <div className="text-green-500 dark:text-green-400">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Next Call Due</p>
              <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                {formatDate(stats.nextCallDate)}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {Math.ceil((stats.nextCallDate - new Date()) / (1000 * 60 * 60 * 24))} days from now
              </p>
            </div>
            <div className="text-blue-500 dark:text-blue-400">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Calls</p>
              <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">{calls.filter(call => call.call_status === 'connected').length}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                {calls.filter(call => call.call_status === 'not_connected').length} not connected
              </p>
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <section className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Contact details</h2>
          <dl className="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Student Number</dt>
              <dd>{student.phone || '—'}</dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Whatsapp</dt>
              <dd>{student.whatsapp || '—'}</dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Parent Number</dt>
              <dd>{student.parent_number || '—'}</dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Course</dt>
              <dd>{student.course || '—'}</dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Campus</dt>
              <dd>{student.campus || '—'}</dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Hostel</dt>
              <dd>{student.hostel || '—'}</dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Mentor</dt>
              <dd className="flex items-center gap-2">
                {isEditingMentor ? (
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedMentor}
                      onChange={(e) => setSelectedMentor(e.target.value)}
                      disabled={updatingMentor}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    >
                      <option value="">Select mentor...</option>
                      {mentorsList.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.full_name || m.email}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleUpdateMentor}
                      disabled={updatingMentor}
                      className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                      title="Save"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setIsEditingMentor(false)}
                      disabled={updatingMentor}
                      className="text-slate-400 hover:text-slate-600"
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <span>{mentorLabel}</span>
                    {role === 'admin' && (
                      <button
                        onClick={handleEditMentorClick}
                        className="text-slate-400 hover:text-brand transition-colors"
                        title="Change mentor"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </dd>
            </div>
            <div className="flex items-start justify-between">
              <dt className="font-medium text-slate-500 dark:text-slate-400">Created</dt>
              <dd>{student.created_at ? formatDate(student.created_at) : '—'}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Call history</h2>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {calls.filter(call => call.call_status === 'connected').length} record{calls.filter(call => call.call_status === 'connected').length === 1 ? '' : 's'}
          </span>
        </div>

        {calls.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            No call recordings have been logged for this student yet.
          </p>
        ) : (
          <ul className="mt-6 space-y-3">
            {calls.map((call) => {
              const isExpanded = expandedCalls.has(call.id);
              return (
                <li
                  key={call.id}
                  className="rounded-xl border border-slate-200 bg-slate-50 shadow-sm transition hover:border-brand/40 dark:border-slate-800 dark:bg-slate-800/40"
                >
                  {/* Collapsed Header */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => toggleCallExpansion(call.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${call.call_status === 'connected'
                        ? 'bg-green-500'
                        : 'bg-red-500'
                        }`} />
                      <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {call.call_status === 'connected' ? 'Connected' : 'Not Connected'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDate(call.created_at)} • by {call.mentor?.full_name || call.mentor?.email || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <button
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                    >
                      <svg
                        className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-700 pt-4">
                      <div className="space-y-4">
                        {/* Notes */}
                        {call.notes && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Notes</h4>
                            <p className="text-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                              {call.notes}
                            </p>
                          </div>
                        )}

                        {/* Attachments */}
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Attachments</h4>
                          <div className="flex flex-wrap gap-2">
                            {call.recordingUrl ? (
                              <a
                                href={call.recordingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand hover:border-brand hover:bg-brand/20 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                                Recording
                              </a>
                            ) : (
                              <span className="text-xs font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-full">
                                Recording unavailable
                              </span>
                            )}
                            {call.screenshotUrl ? (
                              <a
                                href={call.screenshotUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 hover:text-brand hover:border-brand/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:text-brand-light transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Screenshot
                              </a>
                            ) : (
                              <span className="text-xs font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-full">
                                No screenshot
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Call Logging Modal */}
      {showCallForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                  Log Call with {student?.name}
                </h2>
                <button
                  onClick={() => setShowCallForm(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  aria-label="Close modal"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Call Status */}
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Call Status</span>
                  <select
                    value={callStatus}
                    onChange={(e) => setCallStatus(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  >
                    <option value="connected">✅ Connected</option>
                    <option value="not_connected">❌ Not Connected</option>
                  </select>
                </label>

                {/* Call Recording */}
                {callStatus === 'connected' && (
                  <label className="block space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Call Recording <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="file"
                      accept="video/*,audio/*"
                      onChange={(e) => handleFileChange(e, setCallFile)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-700 focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand-dark"
                      required
                    />
                    {callFile && (
                      <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                        ✓ {callFile.name}
                      </p>
                    )}
                    <p className="text-xs text-red-600 dark:text-red-400 mt-2">🎵 Required for connected calls</p>
                  </label>
                )}

                {/* Screenshot */}
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Screenshot {callStatus === 'not_connected' && <span className="text-red-500">*</span>}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, setScreenshotFile)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-700 focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-500 file:text-white hover:file:bg-slate-600"
                    required={callStatus === 'not_connected'}
                  />
                  {screenshotFile && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                      ✓ {screenshotFile.name}
                    </p>
                  )}
                  {callStatus === 'not_connected' && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-2">📷 Required for not connected calls</p>
                  )}
                </label>

                {/* Notes */}
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Notes</span>
                  <textarea
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                    placeholder="How did the call go? Any important updates or concerns?"
                    rows={4}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 resize-none"
                  />
                </label>

                {/* Call Category (only for connected calls) */}
                {callStatus === 'connected' && (
                  <label className="block space-y-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Call Category</span>
                    <select
                      value={callTag}
                      onChange={(e) => setCallTag(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-brand focus:ring-2 focus:ring-brand/40 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    >
                      <option value="">Select category...</option>
                      <option value="refund_requested">💰 Refund Requested</option>
                    </select>
                  </label>
                )}

                {/* Progress Bar */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                      <span>Uploading files...</span>
                      <span>{uploadProgress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2 dark:bg-slate-700">
                      <div
                        className="bg-brand h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCallForm(false)}
                    disabled={uploading}
                    className="flex-1 px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadCall}
                    disabled={uploading || (callStatus === 'connected' && !callFile) || (callStatus === 'not_connected' && !screenshotFile)}
                    className="flex-1 px-6 py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60 disabled:cursor-not-allowed transition active:scale-95"
                  >
                    {uploading ? '📤 Uploading...' : '📤 Log Call'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
